import { useEffect, useState } from "react";
import { toast } from "sonner";
import axios from "axios";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Loader2, ArrowUpRight } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIES = [
  "CPU",
  "SSD / Storage",
  "Memory / DRAM",
  "GPU",
  "Integrated Circuits",
  "Motherboards",
  "Networking",
  "Enterprise Storage",
  "Industrial Components",
  "Other",
];

export default function QuoteDialog({ open, onOpenChange, defaultCategory }) {
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    name: "",
    company: "",
    email: "",
    phone: "",
    country: "",
    category: defaultCategory || "",
    quantity: "",
    message: "",
  });

  const handleChange = (k) => (e) =>
    setForm((f) => ({ ...f, [k]: e.target?.value ?? e }));

  useEffect(() => {
    if (open && defaultCategory) {
      setForm((f) => ({ ...f, category: defaultCategory }));
    }
  }, [open, defaultCategory]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.company || !form.email || !form.category || !form.message) {
      toast.error("Please fill in the required fields.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/quotes`, form);
      toast.success("Quote request received. Our desk will respond within 24 hours.");
      onOpenChange(false);
      setForm({
        name: "",
        company: "",
        email: "",
        phone: "",
        country: "",
        category: defaultCategory || "",
        quantity: "",
        message: "",
      });
    } catch (err) {
      toast.error(
        err?.response?.data?.detail || "Something went wrong. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="quote-dialog"
        className="on-dark max-w-2xl bg-ink-900/95 border-white/10 text-slate-100 backdrop-blur-xl"
      >
        <DialogHeader className="text-left">
          <div className="text-[10px] font-mono tracking-[0.2em] uppercase text-electric-500 mb-2">
            / 24-Hour Quote Desk
          </div>
          <DialogTitle className="font-display text-3xl leading-tight tracking-tight">
            Tell us what you need.
          </DialogTitle>
          <DialogDescription className="text-slate-400">
            Share your requirements — component, quantity, destination. Our
            global sourcing team responds in under 24 hours with pricing,
            availability and lead times.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-2">
          <Field label="Full name *" htmlFor="q-name">
            <Input
              id="q-name"
              data-testid="quote-name-input"
              value={form.name}
              onChange={handleChange("name")}
              placeholder="Jane Chen"
              className="bg-white/[0.03] border-white/10 text-slate-100 placeholder:text-slate-500 focus-visible:ring-electric-500"
              required
            />
          </Field>
          <Field label="Company *" htmlFor="q-company">
            <Input
              id="q-company"
              data-testid="quote-company-input"
              value={form.company}
              onChange={handleChange("company")}
              placeholder="Acme Electronics Mfg."
              className="bg-white/[0.03] border-white/10 text-slate-100 placeholder:text-slate-500 focus-visible:ring-electric-500"
              required
            />
          </Field>
          <Field label="Work email *" htmlFor="q-email">
            <Input
              id="q-email"
              data-testid="quote-email-input"
              type="email"
              value={form.email}
              onChange={handleChange("email")}
              placeholder="jane@acme.com"
              className="bg-white/[0.03] border-white/10 text-slate-100 placeholder:text-slate-500 focus-visible:ring-electric-500"
              required
            />
          </Field>
          <Field label="Phone" htmlFor="q-phone">
            <Input
              id="q-phone"
              data-testid="quote-phone-input"
              value={form.phone}
              onChange={handleChange("phone")}
              placeholder="+1 555 010 2020"
              className="bg-white/[0.03] border-white/10 text-slate-100 placeholder:text-slate-500 focus-visible:ring-electric-500"
            />
          </Field>
          <Field label="Country / Region" htmlFor="q-country">
            <Input
              id="q-country"
              data-testid="quote-country-input"
              value={form.country}
              onChange={handleChange("country")}
              placeholder="Singapore"
              className="bg-white/[0.03] border-white/10 text-slate-100 placeholder:text-slate-500 focus-visible:ring-electric-500"
            />
          </Field>
          <Field label="Product category *">
            <Select
              value={form.category}
              onValueChange={(v) => setForm((f) => ({ ...f, category: v }))}
            >
              <SelectTrigger
                data-testid="quote-category-select"
                className="bg-white/[0.03] border-white/10 text-slate-100"
              >
                <SelectValue placeholder="Select category" />
              </SelectTrigger>
              <SelectContent className="bg-ink-800 border-white/10 text-slate-100">
                {CATEGORIES.map((c) => (
                  <SelectItem key={c} value={c} className="focus:bg-electric-500/10 focus:text-white">
                    {c}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Approx. quantity" htmlFor="q-qty">
            <Input
              id="q-qty"
              data-testid="quote-quantity-input"
              value={form.quantity}
              onChange={handleChange("quantity")}
              placeholder="500 units / month"
              className="bg-white/[0.03] border-white/10 text-slate-100 placeholder:text-slate-500 focus-visible:ring-electric-500"
            />
          </Field>
          <div className="md:col-span-2">
            <Field label="What are you sourcing? *" htmlFor="q-msg">
              <Textarea
                id="q-msg"
                data-testid="quote-message-input"
                rows={4}
                value={form.message}
                onChange={handleChange("message")}
                placeholder="Part numbers, target price, delivery destination, timeline…"
                className="bg-white/[0.03] border-white/10 text-slate-100 placeholder:text-slate-500 focus-visible:ring-electric-500"
                required
              />
            </Field>
          </div>

          <div className="md:col-span-2 flex flex-col md:flex-row md:items-center md:justify-between gap-4 pt-2">
            <p className="text-[11px] text-slate-500 font-mono tracking-wider">
              // Response SLA — under 24 hours worldwide
            </p>
            <button
              type="submit"
              data-testid="quote-submit-button"
              disabled={loading}
              className="btn-electric inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-medium disabled:opacity-70"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" /> Sending
                </>
              ) : (
                <>
                  Send request <ArrowUpRight className="w-4 h-4" />
                </>
              )}
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, htmlFor, children }) {
  return (
    <div className="flex flex-col gap-1.5">
      <Label
        htmlFor={htmlFor}
        className="text-[11px] font-mono tracking-[0.15em] uppercase text-slate-400"
      >
        {label}
      </Label>
      {children}
    </div>
  );
}
